<template>
  <v-container fluid>
    <v-card flat>
      <v-card-title>
        Customer Tracking Summary
        <v-spacer/>
        <v-switch
          v-model="showAll"
          label="All time"
          hide-details
          class="mr-4 mt-0"
        />
        <template v-if="!showAll">
          <v-dialog
            v-model="showDatePicker"
            ref="dialog"
            width="290px"
            persistent
          >
            <v-date-picker
              v-model="datePickerDate"
              range
              no-title
              @close="closeDatePicker()"
              @change="closeDatePicker"
            ></v-date-picker>
            <v-btn @click="closeDatePicker">Close</v-btn>
          </v-dialog>
          <v-text-field
            :value="datesFormatted"
            label="Date range"
            single-line
            hide-details
            readonly
            @click="showDatePicker = true"
            append-icon="mdi-calendar"
            class="mr-4"
            style="max-width: 300px"
          />
        </template>
        <v-btn @click="fetchSummaryData" :loading="loading" color="primary">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>
      
      <v-card-subtitle v-if="summaryData">
        Total Users: {{ summaryData.totalUsers }}
      </v-card-subtitle>
      
      <v-card-text v-if="summaryData && !loading">
        <v-row>
          <v-col cols="6" md="4" v-for="category in categories" :key="category.key">
            <v-card outlined class="mb-4">
              <v-card-title class="pb-2">
                <v-icon left>{{ category.icon }}</v-icon>
                {{ category.title }}
              </v-card-title>
              <v-card-text class="pt-0">
                <div v-if="summaryData[category.key] && summaryData[category.key].length > 0">
                  <div v-for="item in summaryData[category.key].slice(0, 10)" :key="item.value" class="mb-2">
                    <div class="d-flex justify-space-between align-center">
                      <div class="text-truncate" style="max-width: 60%;">
                        <strong>{{ formatValue(item.value, category.key) }}</strong>
                      </div>
                      <div class="text-right">
                        <div class="text-body-2">{{ item.count }} ({{ item.percentage }}%)</div>
                        <v-progress-linear
                          :value="parseFloat(item.percentage)"
                          height="4"
                          color="primary"
                          background-color="grey lighten-3"
                        ></v-progress-linear>
                      </div>
                    </div>
                  </div>
                  <div v-if="summaryData[category.key].length > 10" class="text-caption text--secondary">
                    And {{ summaryData[category.key].length - 10 }} more...
                  </div>
                </div>
                <div v-else class="text-center text--secondary">
                  No data available
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <v-skeleton-loader v-if="loading" type="article@6"></v-skeleton-loader>
    
    <v-alert v-if="error" type="error" class="mt-4">
      {{ error }}
    </v-alert>
  </v-container>
</template>

<script>
import api from '../api'
import moment from 'moment'

export default {
  name: 'CustomerTrackingSummary',
  data() {
    return {
      loading: false,
      error: null,
      summaryData: null,
      showAll: true,
      dates: [
        moment().subtract(2, 'months').format(moment.HTML5_FMT.DATE),
        moment().format(moment.HTML5_FMT.DATE)
      ],
      showDatePicker: false,
      datePickerDate: null,
      categories: [
        { key: 'browser', title: 'Browser', icon: 'mdi-web' },
        { key: 'os', title: 'Operating System', icon: 'mdi-monitor' },
        { key: 'device', title: 'Device Type', icon: 'mdi-devices' },
        { key: 'userAgent', title: 'Full User Agent', icon: 'mdi-information' },
        { key: 'platform', title: 'Platform', icon: 'mdi-monitor' },
        { key: 'origin', title: 'Origin', icon: 'mdi-earth' },
        { key: 'build', title: 'Build', icon: 'mdi-hammer-wrench' },
        { key: 'buildVersion', title: 'Build Version', icon: 'mdi-numeric' },
        { key: 'capacitor', title: 'Capacitor', icon: 'mdi-cellphone' },
        { key: 'referer', title: 'Referer', icon: 'mdi-link' },
        { key: 'hostname', title: 'Hostname', icon: 'mdi-server' }
      ]
    }
  },
  mounted() {
    this.fetchSummaryData()
  },
  watch: {
    showAll() {
      this.fetchSummaryData()
    },
    showDatePicker(v) {
      if (v) {
        this.datePickerDate = this.dates
      } else {
        this.dates = this.datePickerDate
      }
    }
  },
  computed: {
    dateFrom() {
      return [...this.dates].sort()[0]
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0]
    },
    datesFormatted() {
      const format = 'MMM D, YYYY'
      if (!this.dateFrom) return ''
      if (this.dateFrom === this.dateTo) return moment(this.dateFrom).format(format)
      return `${moment(this.dateFrom).format(format)} – ${moment(this.dateTo).format(format)}`
    }
  },
  methods: {
    async fetchSummaryData() {
      this.loading = true
      this.error = null

      try {
        const params = this.showAll ? {} : { from: this.dateFrom, to: this.dateTo }
        const response = await api.get('/v2/tracking/summary', { params })
        this.summaryData = response.data
      } catch (error) {
        console.error('Error fetching summary data:', error)
        this.error = 'Failed to load tracking summary data'
      } finally {
        this.loading = false
      }
    },
    closeDatePicker() {
      this.showDatePicker = false
      this.$nextTick(() => this.fetchSummaryData())
    },
    
    formatValue(value, category) {
      if (category === 'userAgent') {
        return this.formatUserAgent(value)
      }
      return value
    },
    
    formatUserAgent(userAgentString) {
      try {
        const UAParser = require('ua-parser-js')
        const parser = new UAParser(userAgentString)
        const result = parser.getResult()
        
        const browser = result.browser.name ? `${result.browser.name} ${result.browser.version || ''}`.trim() : 'Unknown Browser'
        const os = result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : 'Unknown OS'
        
        return `${browser} on ${os}`
      } catch (e) {
        return userAgentString.length > 50 ? userAgentString.substring(0, 50) + '...' : userAgentString
      }
    }
  }
}
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>